import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import { Autocomplete, type AutocompleteProps } from "./AutocompleteSelect";
import { toMapFn } from "../../../utils/reducers";
import { TextWithHighlight } from "../TextWithHighlight";
import { makeStyles } from "@mui/styles";
import { C_DROPDOWN_HOVER, C_PRIMARY, C_PRIMARY_HOVER, C_SECONDARY_DISABLED } from "../icon/Colors";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";

interface Props<FreeSolo extends boolean | undefined>
  extends Omit<
    AutocompleteProps<string, false, boolean, FreeSolo>,
    "options" | "renderOption" | "onChange" | "value" | "multiple" | "loadingText"
  > {
  values: DropdownEntry<string | number>[];
  value?: string | number;
  onChange?: (value: string | number) => void;
}

export const useSimpleAutocompleteStyles = makeStyles({
  option: {
    background: "none !important",
    '&[data-focus="true"]': {
      backgroundColor: `${C_DROPDOWN_HOVER} !important`,
    },
  },
  endAdornment: {
    "& .Mui-disabled": {
      "& .scha-icon-chevron-down": {
        color: C_SECONDARY_DISABLED,
      },
    },
    "& .scha-icon-chevron-down": {
      color: C_PRIMARY,
      "&:hover": {
        color: C_PRIMARY_HOVER,
      },
    },
  },
});

type OnChangeType = AutocompleteProps<string, false, boolean, undefined>["onChange"];

export const SimpleAutocomplete = <FreeSolo extends boolean | undefined = undefined>({
  values,
  value: valueProp,
  onInputChange,
  onChange: onChangeProp,
  ...rest
}: Props<FreeSolo>) => {
  const classes = useSimpleAutocompleteStyles();

  const { t } = useTranslation();
  // @ts-ignore
  const [inputValue, setInputValue] = useState<string>(rest.value || rest.defaultValue);

  const { options, optionMap } = useOptions(values, (values) => values.stringLabel ?? "");
  const { optionMap: valueMap } = useOptions(values, (values) => values.value ?? "");

  const onChange: OnChangeType = useCallback(
    (_: ChangeEvent<unknown>, label: string | null) => {
      const val = optionMap[label!].value;
      onChangeProp?.(val);
    },
    [onChangeProp, optionMap],
  );

  const value = valueMap[valueProp ?? ""]?.stringLabel ?? "";

  return (
    <Autocomplete
      loadingText={t("spinner.text")}
      disableClearable={true}
      autoHighlight={true}
      classes={classes}
      value={value}
      getOptionLabel={(value) => {
        return optionMap[value]?.stringLabel || "";
      }}
      onInputChange={(e, value, reason) => {
        setInputValue(value);
        onInputChange?.(e, value, reason);
      }}
      options={options}
      renderOption={(props, value) => {
        return (
          <li {...props}>
            <TextWithHighlight highlight={inputValue}>{value}</TextWithHighlight>
          </li>
        );
      }}
      onChange={onChange}
      {...rest}
    />
  );
};

export function useOptions<T extends object>(values: T[], keyFunc: (obj: T) => string | number) {
  return useMemo(() => {
    const optionMap = toMapFn(values, keyFunc);

    return {
      options: Object.keys(optionMap),
      optionMap,
    };
  }, [values, keyFunc]);
}
